import { UcoastVideo } from '@/scripts/core/ucoast-video'
import type {
	DebounceCallback,
} from '@/scripts/types/theme'
import { type ProductModel } from '@/scripts/optional/product-model'
import { type ProductVariant } from '@/scripts/types/api'
import { MediaManager } from '@/scripts/core/media'
import { ArtDirection } from '@/scripts/core/art-direction'
import { TsDOM as q } from '@/scripts/core/TsDOM'
// CONSTANTS
export const ON_CHANGE_DEBOUNCE_TIMER = 300

export const PUB_SUB_EVENTS = {
	cartUpdate: 'cart-update',
	quantityRangeUpdate: 'quantity-range-update',
	quantityValueUpdate: 'quantity-value-update',
	variantChange: 'variant-change',
	variantChangeImmediate: 'variant-change-immediate',
	cartError: 'cart-error',
}

//  common attributes. sometimes these are used as elements selectors, if the attribute is added/removed
export const ATTRIBUTES = {
	cartEmpty: 'data-uc-cart-empty',
	loading: 'data-uc-loading',
	submenu: 'data-uc-submenu',
	menuOpening: 'data-uc-menu-opening',
}

// selectors for common elements where the selector won't be added/removed
export const SELECTORS = {
	cartLink: '[data-uc-cart-icon-bubble]',
	loadingOverlay: '[data-uc-loading-overlay]',
	loadingOverlaySpinner: '[data-uc-loading-overlay-spinner]',
	sectionHeader: '.section-header',
	headerWrapper: '[data-uc-header-wrapper]',
}

// PUB SUB
// todo: add additional data types as needed

export type CartUpdateEvent = {
	source: 'cart-items' | 'product-form'
	productVariantId?: string
}

export type CartErrorEvent = {
	source: 'product-form'
	productVariantId: string
	errors: string | string[] | { [key: string]: string[] }
	message: string
}

export type VariantChangeImmediateEvent = {
	source: 'variant-change-immediate'
	data: {
		variantId: number
	}
}

export type VariantChangeEvent = {
	source: 'product-form'
	data: {
		sectionId: string
		html: Document
		variant: ProductVariant
		currentQuantity?: number
	}
}

export type QuantityChangeEvent = {
	source: 'quantity-change'
	data: {
		quantity: number
	}
}

export type PubSubEvent =
	| CartUpdateEvent
	| CartErrorEvent
	| VariantChangeEvent
	| QuantityChangeEvent
	| VariantChangeImmediateEvent
	| undefined

// typeguards for events

export function isCartUpdateEvent(obj: PubSubEvent): obj is CartUpdateEvent {
	if (!obj) return false
	if (!('source' in obj)) return false
	if (!('productVariantId' in obj)) return false
	return true
}

export function isCartErrorEvent(obj: PubSubEvent): obj is CartErrorEvent {
	if (!obj) return false
	if (!('source' in obj)) return false
	if (!('productVariantId' in obj)) return false
	if (!('message' in obj)) return false
	if (!('errors' in obj)) return false
	return true
}

export function createVariantChangeEvent(event: VariantChangeEvent) {
	if (!isVariantChangeEvent(event)) {
		throw new Error('Event is not a VariantChangeEvent')
	}
	return event
}

export function isVariantChangeEvent(
	obj: PubSubEvent
): obj is VariantChangeEvent {
	if (!obj) return false
	if (!('data' in obj)) return false
	if (!('sectionId' in obj.data)) return false
	if (!('html' in obj.data)) return false
	if (!('variant' in obj.data)) return false
	return true
}

export function createVariantChangeImmediateEvent(
	event: VariantChangeImmediateEvent
) {
	if (!isVariantChangeImmediateEvent(event)) {
		throw new Error('Event is not a VariantChangeEvent')
	}
	return event
}
export function isVariantChangeImmediateEvent(
	obj: PubSubEvent
): obj is VariantChangeImmediateEvent {
	if (!obj) return false
	if (!('data' in obj)) return false
	if (!('variantId' in obj.data)) return false
	return true
}

export type SubscriberCallback = (pubSubEvent: PubSubEvent) => void

let subscribers: Record<string, SubscriberCallback[]> = {}

export function subscribe(eventName: string, callback: SubscriberCallback) {
	if (subscribers[eventName] === undefined) {
		subscribers[eventName] = []
	}

	subscribers[eventName] = [...subscribers[eventName], callback]

	return function unsubscribe() {
		subscribers[eventName] = subscribers[eventName].filter((cb) => {
			return cb !== callback
		})
	}
}

export function publish(
	eventName: string,
	pubSubEvent: PubSubEvent = undefined
) {
	if (subscribers[eventName]) {
		subscribers[eventName].forEach((callback) => {
			callback(pubSubEvent)
		})
	}
}

// browser safe replace all
export const replaceAll = (str: string, find: string, replace: string) => {
	return str.split(find).join(replace)
}

// scroll to anchor link with some ucoast-specific features, not used in default dawn repack
export const scrollToAnchor = (selector: string) => {
	const header = q.rs('[data-uc-header-wrapper]')
	const anchor = q.rs(selector)
	const headerHeight = header.offsetHeight
	const collectionTop = anchor.getBoundingClientRect().top
	const scrollToPosition =
		collectionTop -
		headerHeight -
		parseFloat(
			window?.getComputedStyle(anchor)?.getPropertyValue('margin-top') ??
				0
		) +
		1
	window.scrollBy({ top: scrollToPosition, behavior: 'smooth' })
}

// TODO: refactor to use app proxy for security
export const getBackendRoute = () => {
	return 'http://localhost:5000'
}

export const getCurrentHeaderHeight = () => {
	let height = 0
	const headerSections = q.rl('.shopify-section-group-header-group')
	headerSections.forEach((section) => {
		const sectionHeight = section.getBoundingClientRect().height
		height += sectionHeight
	})
	return height
}

// these three functions are somewhat similar - should be refactored for clarity
export const isElementInViewport = (el: HTMLElement) => {
	const rect = el.getBoundingClientRect()
	const result =
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <=
			(window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <=
			(window.innerWidth || document.documentElement.clientWidth)
	return result
}

export const isInViewport = (
	element: HTMLElement,
	rect = element.getBoundingClientRect()
) => {
	return (
		rect.top <
			(window.innerHeight || document.documentElement.clientHeight) &&
		rect.left <
			(window.innerWidth || document.documentElement.clientWidth) &&
		rect.bottom > 0 &&
		rect.right > 0
	)
}

export const isIntersecting = async (
	element: HTMLElement,
	threshold?: number
): Promise<boolean> => {
	return new Promise<boolean>((resolve) => {
		const observer = new IntersectionObserver(
			(entries) => {
				resolve(entries[0].isIntersecting)
				observer.disconnect()
			},
			{ threshold }
		)
		observer.observe(element)
	})
}

export function getViewportBounds() {
	return {
		width: window.innerWidth || document.documentElement.clientWidth,
		height: window.innerHeight || document.documentElement.clientHeight,
	}
}

export const isTenPercentInViewport = (
	element: HTMLElement,
	rect = element.getBoundingClientRect(),
	windowBounds = getViewportBounds()
) => {
	if (!isInViewport(element, rect)) {
		return false
	}
	const { width: viewportWidth, height: viewportHeight } = windowBounds

	const elementHeight = rect.height
	const elementWidth = rect.width

	const visibleHeight =
		Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
	const visibleWidth =
		Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0)

	const visibleArea = Math.max(0, visibleHeight) * Math.max(0, visibleWidth)
	const elementArea = elementHeight * elementWidth

	return visibleArea >= 0.1 * elementArea
}

// this function applies the scaling from the --ax variables. it is project-dependent
// 1440 and 375 are specific to the size I request from designers, however should be refactored as Constants
// TODO: add const defs for hard coded scaling values

export const scaleValue = (val: number, viewport: 'mobile' | 'desktop') => {
	let ratio =
		viewport === 'desktop'
			? 1440 / window.innerWidth
			: 375 / window.innerWidth
	if (viewport === 'desktop' && ratio > 1.25) ratio = 1.25 // this is a reverse of the 1:.8 ratio from desktop min in css
	if (viewport === 'desktop' && ratio < 1) ratio = 1 // this is a verse of the 1:1.15 ratio from desktop max in css
	if (viewport === 'mobile' && ratio > 1.1111111111) ratio = 1.1111111111
	if (viewport === 'mobile' && ratio < 1) ratio = 1
	return (val * ratio).toFixed(1)
}

// TODO: refactor for default dawn repack
// this sets all relevant height variables for the app

export const setHeightVars = (
	header: HTMLElement,
	announcement: HTMLElement | undefined
) => {
	const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile'
	const viewportHeight =
		viewport === 'desktop'
			? '100vh'
			: `calc(${scaleValue(window.innerHeight, viewport)} * var(--ax))`
	document.documentElement.style.setProperty(
		'--viewport-height',
		viewportHeight
	)
	document.documentElement.style.setProperty(
		'--header-height',
		`calc(${scaleValue(header.getBoundingClientRect().height, viewport)} * var(--ax))`
	)

	if (announcement) {
		document.documentElement.style.setProperty(
			'--announcement-height',
			`calc(${scaleValue(announcement.getBoundingClientRect().height, viewport)} * var(--ax))`
		)
	}
}

// set drawer height when opened - this prevents the drawer from being too short on mobile
// tbh, shouldn't be necessary with proper height vars, but keeping it here since shopify uses it
export const setDrawerHeight = () => {
	document.documentElement.style.setProperty(
		'--drawer-height',
		`${window.innerHeight}px`
	)
}

// accessibility functions

export function initializeSummaryA11y() {
	// this is from shopify
	// it adds some accessibility features to summary elements
	document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
		summary.setAttribute('role', 'button')
		const parentNode =
			summary.parentNode instanceof HTMLElement
				? summary.parentNode
				: undefined
		const ariaExpandedVal =
			parentNode && parentNode.hasAttribute('open') ? 'true' : 'false'
		summary.setAttribute('aria-expanded', ariaExpandedVal)

		const nextElementSibling =
			summary.nextElementSibling instanceof HTMLElement
				? summary.nextElementSibling
				: undefined

		if (nextElementSibling && nextElementSibling.hasAttribute('id')) {
			summary.setAttribute(
				'aria-controls',
				q.ra(nextElementSibling, 'id')
			)
		}
		summary.addEventListener('click', (event) => {
			const currentTarget = q.rct(event)
			const closestTarget = q.rClosestTarget(event, 'details')
			currentTarget.setAttribute(
				'aria-expanded',
				`${!closestTarget.hasAttribute('open')}`
			)
		})
		if (summary.closest('header-drawer, menu-drawer')) return

		const parentElement =
			summary.parentElement instanceof HTMLElement
				? summary.parentElement
				: undefined
		if (!parentElement) return
		parentElement.addEventListener('keyup', q.onKeyUpEscape)
	})
}


export function focusVisiblePolyfill() {
	const navKeys = [
		'ARROWUP',
		'ARROWDOWN',
		'ARROWLEFT',
		'ARROWRIGHT',
		'TAB',
		'ENTER',
		'SPACE',
		'ESCAPE',
		'HOME',
		'END',
		'PAGEUP',
		'PAGEDOWN',
	]
	let currentFocusedElement: HTMLElement | Element | null = null
	let mouseClick: boolean | null = null

	window.addEventListener('keydown', (event) => {
		if (navKeys.includes(event.code.toUpperCase())) {
			mouseClick = false
		}
	})

	window.addEventListener('mousedown', (_event: MouseEvent) => {
		mouseClick = true
	})

	window.addEventListener(
		'focus',
		() => {
			if (currentFocusedElement)
				currentFocusedElement.classList.remove('focused')

			if (mouseClick) return

			currentFocusedElement = document.activeElement
			if (currentFocusedElement instanceof HTMLElement) {
				currentFocusedElement.classList.add('focused')
			}
		},
		true
	)
}

export function onKeyUpEscape(event: KeyboardEvent) {
	if (event.code.toUpperCase() !== 'ESCAPE') return

	const openDetailsElement = q.oClosestTarget(event, 'details[open]')
	if (!openDetailsElement) return

	const summaryElement = q.rs('summary', openDetailsElement)
	openDetailsElement.removeAttribute('open')
	summaryElement.setAttribute('aria-expanded', 'false')
	summaryElement.focus()
}

export function pauseAllMedia() {
	const jsYoutubeEls = q.ol<HTMLIFrameElement>('.js-youtube')
	if (jsYoutubeEls) {
		jsYoutubeEls.forEach((video) => {
			if (!video.contentWindow) return
			video.contentWindow.postMessage(
				'{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
				'*'
			)
		})
	}

	const jsVimeoEls = q.ol<HTMLIFrameElement>('.js-vimeo')
	if (jsVimeoEls) {
		jsVimeoEls.forEach((video) => {
			if (!video.contentWindow) return
			video.contentWindow.postMessage('{"method":"pause"}', '*')
		})
	}

	const html5Videos = q.ol<HTMLVideoElement>('video')
	if (html5Videos) {
		html5Videos.forEach((video) => video.pause())
	}

	const productModels = q.ol<ProductModel>('product-model')
	if (productModels) {
		productModels.forEach((model) => {
			if (model.modelViewerUI) model.modelViewerUI.pause()
		})
	}
}

// event based functions

export function debounce(fn: Function, wait: number): DebounceCallback {
	let timeoutId: number
	return (...args) => {
		clearTimeout(timeoutId)
		// @ts-ignore
		// since this is a generic function that can literally be used for anything, I think 'any' is the best type here
		// 'unknown' would be better but we can't assign a type without moving off of arrow functions
		timeoutId = setTimeout(() => fn.apply(this, args), wait)
	}
}

export function throttle(fn: Function, delay: number = 0) {
	let lastCall = 0
	return function (...args: unknown[]) {
		const now = new Date().getTime()
		if (now - lastCall < delay) {
			return
		}
		lastCall = now
		return fn(...args)
	}
}

// fetch API configs

export function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: `application/${type}`,
		},
	}
}

export function getItemEl(variantId: string, sellingPlanId?: string) {
	return sellingPlanId
		? q.os(
				`[data-uc-cart-item][data-uc-cart-volume-variant="${variantId}"][data-uc-cart-volume-selling-plan-id="${sellingPlanId}"`
			)
		: q.os(
				`[data-uc-cart-item][data-uc-cart-volume-variant="${variantId}"]`
			)
}

export function getItemQuantity(
	variantId: string,
	sellingPlanId?: string
): number {
	const item = getItemEl(variantId, sellingPlanId)
	if (!item) {
		return 0
	}
	const num = parseInt(
		q.oa(item, 'data-uc-cart-volume-quantity') ?? '0'
	)
	if (isNaN(num)) {
		return 0
	}
	return num
}

export function cartItemIsTrial(
	variantId: string,
	sellingPlanId?: string
): boolean {
	const item = getItemEl(variantId, sellingPlanId)
	if (!item) {
		return false
	}

	return q.oa(item, 'data-uc-cart-is-trial') !== undefined
}

export function getQuantityFromCart(
	formQuantity: number,
	variantId: string,
	sellingPlanId?: string
) {
	const item = getItemEl(variantId, sellingPlanId)
	if (!item) {
		console.log('no item with break')
		return formQuantity
	}

	const quantityInCart = getItemQuantity(variantId, sellingPlanId)

	if (formQuantity <= quantityInCart) {
		// in this case, add formQuantity to cart
		return formQuantity
	}

	const quantityToAdd = formQuantity - quantityInCart
	const minString = q.oa(
		item,
    'data-uc-cart-volume-break-min'
	)
	if (!minString) {
		return quantityToAdd
	}
	const minInt = parseInt(minString)
	if (quantityToAdd < minInt) {
		return minInt
	}

	if (quantityToAdd % minInt !== 0) {
		return minInt
		// this is kind of a bug...like, if you had 3 in the cart adn tried to add it wouldnt work exactly
		// but......not something likely to happen and it prevents a much worse issue
	}
	return quantityToAdd
}

export interface CartItem {
	quantity: number
	selling_plan?: number
	id: number
	properties?: {
		_upsell_from_variant?: number
		_refill_variant_id?: number
		_parent_variant_id?: number
	}
}
export function rectifyItemQuantity(body: FormData, quantity = 1): CartItem[] {
	const definedSellingPlan = q.ofd(body, 'selling_plan')
	const sellingPlanId = parseInt(definedSellingPlan)
	const quantityFromCart = getQuantityFromCart(
		quantity,
		q.rfd(body, 'id'),
		definedSellingPlan
	)
	return definedSellingPlan &&
		definedSellingPlan !== '' &&
		!Number.isNaN(sellingPlanId)
		? [
				{
					quantity: quantityFromCart,
					id: parseInt(q.rfd(body, 'id')),
					selling_plan: parseInt(definedSellingPlan),
				},
			]
		: [
				{
					quantity: quantityFromCart,
					id: parseInt(q.rfd(body, 'id')),
				},
			]
}

// klaviyo & notify me not part of initial dawn but will be in every project

type AddToKlaviyoListFormValues = {
	type: 'subscription'
	attributes: {
		custom_source: string
		profile: {
			data: {
				type: 'profile'
				attributes: {
					email: string
				}
			}
		}
	}
	relationships: {
		list: {
			data: {
				type: 'list'
				id: string
			}
		}
	}
}

export interface AddToKlaviyoListParams {
	listId: string
	email: string
	source: string
}

export function addToKlaviyoListConfig({
	source,
	email,
	listId,
}: AddToKlaviyoListParams) {
	const data: AddToKlaviyoListFormValues = {
		type: 'subscription',
		attributes: {
			custom_source: source,
			profile: {
				data: {
					type: 'profile',
					attributes: {
						email,
					},
				},
			},
		},
		relationships: {
			list: {
				data: {
					type: 'list',
					id: listId,
				},
			},
		},
	}
	return {
		method: 'POST',
		mode: 'cors' as RequestMode,
		headers: {
			'Content-Type': 'application/json',
			revision: '2023-12-15',
		},
		body: JSON.stringify({
			data,
		}),
	}
}

type NotifyMeConfigValues = {
	email: string
	variant: string
}

export function notifyMeConfig(body: FormData) {
	const data: NotifyMeConfigValues = {
		email: q.rfd(body, 'email'),
		variant: q.rfd(body, 'variant'),
	}
	return {
		method: 'POST',
		mode: 'cors' as RequestMode,
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	}
}


function initGlobalUcoast() {
	q.safeDefineElement(ArtDirection)
	q.safeDefineElement(UcoastVideo)
	const iOS = window.Ucoast?.iOS ?? false
	const Ucoast: typeof window.Ucoast = {
		shopifyConsentAPILoaded: false,
		iOS,
		editor: {
			selectedBlock: undefined,
		},
		screen: {
			width: window.innerWidth,
			height: window.innerHeight,
			portrait: window.innerHeight > window.innerWidth,
		},
		mediaManager: new MediaManager(),
	}
  if (!window.routes) {
    window.routes = {
      cart_add_url: '/cart/add',
      cart_change_url: '/cart/change',
      cart_update_url: '/cart/update',
      cart_url: '/cart',
      predictive_search_url: '/search/suggest',
      root: '/',
    }
  }
	if (!window.Ucoast) {
		window.Ucoast = Ucoast
	}
	if (!window.Ucoast.mediaManager) {
		window.Ucoast.mediaManager = new MediaManager()
	}
}

export function globalSetup() {
	if (!window.TsDOM) {
		window.TsDOM = new q()
	}
	initGlobalUcoast()
	// mediaLoader
	void window.Ucoast.mediaManager.initialLoad()
	initializeSummaryA11y()
	try {
		document.querySelector(':focus-visible')
	} catch (e) {
		focusVisiblePolyfill()
	}
}
